import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Clock, X } from 'lucide-react';
import { useDataProvider, useGetList, useNotify, useRecordContext } from 'ra-core';
import { useState } from 'react';

const APPROVAL_LEVELS = [
    { level: 1, name: 'Manager Approval' },
    { level: 2, name: 'Lead Designer Approval' },
    { level: 3, name: 'Quality Approval' },
];

export const DesignApprovalWorkflow = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    const { data: steps, isLoading, refetch } = useGetList('design_approval_steps', {
        pagination: { page: 1, perPage: 10 },
        sort: { field: 'level', order: 'ASC' },
        filter: { design_request_id: record?.id },
    });

    if (!record || isLoading) return null;

    // Determine current level needed
    // The current level is the lowest level that is 'pending' or doesn't exist yet
    // Actually, if a step doesn't exist, we assume it's pending for that level.
    const stepsByLevel = (steps || []).reduce((acc: any, step: any) => {
        acc[step.level] = step;
        return acc;
    }, {});

    let currentLevel = 1;
    let isFullyApproved = true;
    for (const lvl of APPROVAL_LEVELS) {
        const step = stepsByLevel[lvl.level];
        if (!step || step.status !== 'approved') {
            currentLevel = lvl.level;
            isFullyApproved = false;
            break;
        }
    }

    const handleAction = async (actionLevel: number, action: 'approved' | 'rejected' | 'revision_requested') => {
        setLoading(true);
        try {
            const existingStep = stepsByLevel[actionLevel];
            
            // 1. Upsert the approval step
            if (existingStep) {
                await dataProvider.update('design_approval_steps', {
                    id: existingStep.id,
                    data: { status: action, acted_at: new Date().toISOString() },
                    previousData: existingStep,
                });
            } else {
                await dataProvider.create('design_approval_steps', {
                    data: {
                        design_request_id: record.id,
                        level: actionLevel,
                        status: action,
                        acted_at: new Date().toISOString(),
                    },
                });
            }

            // 2. If rejected or revision requested, update the main design request
            if (action === 'rejected' || action === 'revision_requested') {
                await dataProvider.update('design_requests', {
                    id: record.id,
                    data: { status: action === 'rejected' ? 'rejected' : 'revision_requested' },
                    previousData: record,
                });
                notify(`Design request ${action.replace('_', ' ')}`, { type: 'success' });
            } 
            // 3. If approved and it was the FINAL level
            else if (action === 'approved' && actionLevel === 3) {
                await dataProvider.update('design_requests', {
                    id: record.id,
                    data: { status: 'approved', completed_at: new Date().toISOString() },
                    previousData: record,
                });
                notify('Design request fully approved', { type: 'success' });
            } else {
                notify(`Level ${actionLevel} approved`, { type: 'success' });
            }

            refetch();
        } catch (error) {
            notify('Error processing approval', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader className="pb-3 border-b bg-muted/50">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Check className="w-4 h-4" /> Approval Workflow
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 relative">
                {/* Connecting line */}
                <div className="absolute left-10 top-12 bottom-12 w-0.5 bg-border" />
                
                <div className="space-y-6">
                    {APPROVAL_LEVELS.map((lvl) => {
                        const step = stepsByLevel[lvl.level];
                        const isCurrent = currentLevel === lvl.level && !isFullyApproved;
                        const isPast = step?.status === 'approved' || (lvl.level < currentLevel && step?.status !== 'rejected');
                        const isRejected = step?.status === 'rejected' || step?.status === 'revision_requested';

                        return (
                            <div key={lvl.level} className="relative z-10 flex gap-4 min-w-0">
                                <div className="flex flex-col items-center pt-1">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shadow-sm
                                        ${isPast ? 'bg-emerald-500 text-white'
                                            : isRejected ? 'bg-red-500 text-white'
                                            : isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900'
                                            : 'bg-card text-muted-foreground border-2 border-border'}`}
                                    >
                                        {isPast ? <Check className="w-4 h-4" /> 
                                            : isRejected ? <X className="w-4 h-4" />
                                            : lvl.level}
                                    </div>
                                </div>
                                
                                <div className={`flex-1 min-w-0 p-4 rounded-lg border ${isCurrent ? 'bg-blue-50/50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 shadow-sm' : 'bg-card border-border'}`}>
                                    <div className="flex justify-between items-start mb-2 gap-2">
                                        <h4 className={`font-bold ${isCurrent ? 'text-blue-900 dark:text-blue-300' : 'text-foreground'}`}>
                                            {lvl.name}
                                        </h4>
                                        <div className="text-xs font-semibold uppercase tracking-wider">
                                            {step?.status === 'approved' && <span className="text-emerald-600">Approved</span>}
                                            {step?.status === 'rejected' && <span className="text-red-600">Rejected</span>}
                                            {step?.status === 'revision_requested' && <span className="text-amber-600">Revision Req.</span>}
                                            {(!step || step?.status === 'pending') && <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>}
                                        </div>
                                    </div>

                                    {step?.acted_at && (
                                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                            <span className="font-medium">Acted at:</span> 
                                            {new Date(step.acted_at).toLocaleString()}
                                        </div>
                                    )}

                                    {/* Action buttons if it is current level */}
                                    {isCurrent && record.status !== 'rejected' && record.status !== 'revision_requested' && (
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="bg-emerald-600 text-white border-transparent hover:bg-emerald-700 hover:text-white h-8"
                                                onClick={() => handleAction(lvl.level, 'approved')}
                                                disabled={loading}
                                            >
                                                <Check className="w-3.5 h-3.5 mr-1" /> Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/70 hover:text-amber-800 dark:hover:text-amber-300 h-8"
                                                onClick={() => handleAction(lvl.level, 'revision_requested')}
                                                disabled={loading}
                                            >
                                                Request Revision
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-950/70 h-8"
                                                onClick={() => handleAction(lvl.level, 'rejected')}
                                                disabled={loading}
                                            >
                                                <X className="w-3.5 h-3.5 mr-1" /> Reject
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};
